import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useConsumeMagicLink } from '../../api/hooks/auth.hook';
import type { ApiErrorResponse } from '../../api/services/auth';

const resolveConsumeError = (error: ApiErrorResponse | unknown): string => {
  if (!error || typeof error !== 'object') {
    return 'We could not reach the server. Please check your connection and try again.';
  }

  const status = (error as ApiErrorResponse).status;
  const code = typeof (error as ApiErrorResponse).error === 'string'
    ? (error as ApiErrorResponse).error
    : undefined;

  if (code === 'token_expired') {
    return 'This link has expired. Please request a new one to sign in.';
  }

  if (code === 'invalid_token' || status === 400) {
    return 'This link is not valid. Please request a new one.';
  }

  if (status === 403 || code === 'no_active_invite') {
    return 'Your email is not authorized yet. Request a new link after receiving an invite.';
  }

  if (status === 401) {
    return 'We could not verify this link. Please request a fresh one to sign in.';
  }

  return 'Something went wrong while signing you in. Please try again.';
};

const isTokenExpired = (error: ApiErrorResponse | unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const code = typeof (error as ApiErrorResponse).error === 'string'
    ? (error as ApiErrorResponse).error
    : undefined;
  return code === 'token_expired';
};

const MagicLinkConfirmPage: React.FC = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { mutateAsync } = useConsumeMagicLink();
  const [message, setMessage] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const token = useMemo(() => new URLSearchParams(search).get('token'), [search]);

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('Missing token. Please use the link from your email.');
      return;
    }

    const verify = async () => {
      setState('loading');
      setMessage(null);
      try {
        await mutateAsync({ token });
        setState('success');
        navigate('/', { replace: true });
      } catch (err) {
        // If token is expired, automatically redirect to signin
        if (isTokenExpired(err)) {
          navigate('/signin', { replace: true });
          return;
        }
        setState('error');
        setMessage(resolveConsumeError(err));
      }
    };

    void verify();
  }, [mutateAsync, navigate, token]);

  if (state === 'loading') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Signing you in…</h1>
          <p className="auth-subtitle">Please wait while we verify your link.</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Unable to sign in</h1>
          <p className="auth-subtitle">{message}</p>
          <button className="auth-submit-btn" onClick={() => navigate('/signin')}>
            Return to sign in
          </button>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <h1 className="auth-title">Link verified</h1>
          <p className="auth-subtitle">Redirecting to your workspace…</p>
        </div>
      </div>
    );
  }

  return null;
};

export default MagicLinkConfirmPage;
